/**
 * Recursively flattens a nested object or array into a single-level object with dot-separated keys.
 * This function handles arrays and objects, converting them into a flat structure.
 * @param obj The object or array to flatten.
 * @param parentKey The base key to use for the current level of recursion (used internally).
 * @param result The accumulator object that holds the flattened key-value pairs (used internally).
 * @returns A single-level object with dot-separated keys representing the nested structure.
 */
function flattenObject(obj, parentKey = "", result = {}) {
	if (Array.isArray(obj)) {
		// Array handling
		obj.forEach((item, index) => {
			const arrayKey = `${parentKey}[${index}]`;
			if (typeof item === "object" && item !== null) {
				flattenObject(item, arrayKey, result);
			} else {
				result[arrayKey] = item;
			}
		});
	} else if (typeof obj === "object" && obj !== null) {
		// Object handling
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				const newKey = parentKey ? `${parentKey}.${key}` : key;
				if (typeof obj[key] === "object" && obj[key] !== null) {
					flattenObject(obj[key], newKey, result);
				} else {
					result[newKey] = obj[key];
				}
			}
		}
	} else {
		// Base case
		result[parentKey] = obj;
	}
	return result;
}

/**
 * Handles form submission failures by logging errors and focusing on the first erroneous field.
 * This function improves user experience by automatically bringing the first error field into view.
 * @param errors An object where keys are field names and values are error messages or error objects.
 * @param document The Document object from the DOM API.
 * @returns void
 */
export const focusFirstErrorField = (errors: { [key: string]: any }) => {
	const flattenedErrors = flattenObject(errors);
	const selector = Object.keys(flattenedErrors)
		.map(fieldName => `[name="${fieldName}"], [id="${fieldName}"]`)
		.join(",");
	const errorEl = document.querySelector(selector) as HTMLElement | null;

	if (errorEl && errorEl.focus) {
		errorEl.focus();
	}
};
